import axios from "axios";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  getDate,
  formatDate,
  formatQuantity,
  getDaysDifference,
  getPOBalance,
} from "../../utils/reportUtils";
import { useLocation, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const PurchaseOrders = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pos, setPos] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardStatus = location.state?.preset;
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(dashboardStatus || "");
  const storedUser = JSON.parse(localStorage.getItem("user"));
  
  const authHeader = {
    headers: {
      Authorization: `Bearer ${storedUser?.accessToken}`,
    },
  };

  const [sort, setSort] = useState({
    key: "deliveryDate",
    direction: 1,
  });

  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());

  useEffect(() => {
    loadPurchaseOrders();
  }, []);

  const loadPurchaseOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/open-po/latest`, authHeader);

      setPos(response.data || []);
    } catch (e) {
      console.error(e);
      setError("Unable to load purchase orders.");
    } finally {
      setLoading(false);
    }
  };

  // PO qty not yet received on GRNs (same as the dashboard)
  const getOpenQuantity = (po) => getPOBalance(po);

  const getOverdueDays = (po) => {
    const open = getOpenQuantity(po);

    if (open <= 0 || !po.deliveryDate) {
      return 0;
    }

    const days = getDaysDifference(today, po.deliveryDate);

    return Math.max(0, days);
  };

  const getPOStatus = (po) => {
    const received = Number(po.quantityDelivered) || 0;
    const open = getOpenQuantity(po);

    if (open <= 0) {
      return "FULLY_RECEIVED";
    }

    if (po.deliveryDate && po.deliveryDate < today) {
      return "OVERDUE";
    }

    // Due soon is checked before partially received, the same order as the dashboard tile.
    if (po.deliveryDate) {
      const days = getDaysDifference(po.deliveryDate, today);

      if (days <= 7) {
        return "DUE_SOON";
      }
    }

    if (received > 0) {
      return "PARTIALLY_RECEIVED";
    }

    return "OPEN";
  };

  const getStatusClass = (status) => {
    if (status === "FULLY_RECEIVED") return "b-grey";
    if (status === "PARTIALLY_RECEIVED") return "b-risk";
    if (status === "DUE_SOON") return "b-risk";
    if (status === "OVERDUE") return "b-short";

    return "b-info";
  };

  const getStatusText = (status) => {
    if (status === "FULLY_RECEIVED") return "Closed";
    if (status === "PARTIALLY_RECEIVED") return "Partially received";
    if (status === "DUE_SOON") return "Due soon";
    if (status === "OVERDUE") return "Overdue";

    return "Open";
  };

  const getReceivedPercentage = (po) => {
    const ordered = Number(po.orderQuantity) || 0;
    const received = Number(po.quantityDelivered) || 0;

    if (ordered <= 0) return 0;

    return Math.min(100, Math.round((received / ordered) * 100));
  };

  const changeSort = (key) => {
    setSort((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction * -1,
        };
      }

      return {
        key,
        direction: 1,
      };
    });
  };

  const getSortValue = (po, key) => {
    if (key === "po") {
      return po.purchasingDocument || "";
    }

    if (key === "material") {
      return po.material || "";
    }

    if (key === "vendor") {
      return po.supplierName || "";
    }

    if (key === "ordered") {
      return Number(po.orderQuantity) || 0;
    }

    if (key === "received") {
      return Number(po.quantityDelivered) || 0;
    }

    if (key === "open") {
      return getOpenQuantity(po);
    }

    if (key === "deliveryDate") {
      return po.deliveryDate || "";
    }

    if (key === "overdue") {
      return getOverdueDays(po);
    }

    if (key === "status") {
      return getPOStatus(po);
    }

    return "";
  };

  const sortPurchaseOrders = (list) => {
    return list.slice().sort((a, b) => {
      const first = getSortValue(a, sort.key);
      const second = getSortValue(b, sort.key);

      if (first < second) {
        return -1 * sort.direction;
      }

      if (first > second) {
        return 1 * sort.direction;
      }

      return 0;
    });
  };

  const filteredPOs = pos.filter((po) => {
    const status = getPOStatus(po);

    if (filter === "OVERDUE" && status !== "OVERDUE") {
      return false;
    }

    if (filter === "DUE_SOON" && status !== "DUE_SOON") {
      return false;
    }

    if (filter === "PARTIALLY_RECEIVED" && status !== "PARTIALLY_RECEIVED") {
      return false;
    }

    // Open: GRN received qty has not reached the PO qty yet.
    if (filter === "OPEN" && status === "FULLY_RECEIVED") {
      return false;
    }

    if (filter === "FULLY_RECEIVED" && status !== "FULLY_RECEIVED") {
      return false;
    }

    if (q) {
      const searchText = [
        po.purchasingDocument,
        po.purchaseRequisition,
        po.itemCode,
        po.material,
        po.shortText,
        po.supplierName,
        po.supplierEmail,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchText.includes(q.toLowerCase())) {
        return false;
      }
    }

    return true;
  });

  const rows = sortPurchaseOrders(filteredPOs);

  const getSortArrow = (key) => {
    if (sort.key !== key) {
      return "";
    }

    return sort.direction === 1 ? " ↑" : " ↓";
  };

  const exportExcel = () => {
    const data = rows.map((po) => {
      const ordered = Number(po.orderQuantity) || 0;
      const received = Number(po.quantityDelivered) || 0;
      const open = getOpenQuantity(po);
      const overdueDays = getOverdueDays(po);
      const status = getPOStatus(po);

      return {
        "PO Number": po.purchasingDocument || "",
        "PR Number": po.purchaseRequisition || "",
        "Item Code": po.itemCode || "",
        "Material Code": po.material || "",
        "Material Description": po.shortText || "",
        "PO Creation Date": po.documentDate ? formatDate(po.documentDate) : "",
        "PO Sent Date": po.poSentDate ? formatDate(po.poSentDate) : "",
        "PO Delivery Date": po.deliveryDate ? formatDate(po.deliveryDate) : "",
        "PO Qty": ordered,
        "Supplier Name": po.supplierName || "",
        "Supplier Email": po.supplierEmail || "",
        Received: received,
        Open: open,
        Overdue: overdueDays > 0 ? overdueDays : "",
        Status: getStatusText(status),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase Orders");

    XLSX.writeFile(workbook, "purchase-orders.xlsx");
  };

  if (loading) {
    return (
      <div className="page">
        <div className="card">
          <div className="empty">
            <strong>Loading purchase orders...</strong>
            Please wait.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="ph">
          <div>
            <h1>Purchase orders</h1>
            <p>Track open purchase orders and supplier deliveries.</p>
          </div>
        </div>

        <div className="note err">
          <b>Unable to load purchase orders.</b>
          <br />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Purchase orders</h1>
          <p>
            <strong>{rows.length}</strong> of {pos.length} PO lines · open
            quantity is ordered minus received
          </p>
        </div>
        <button className="btn btn-o" onClick={exportExcel}>
          Export to Excel
        </button>
      </div>

      <div className="ctl">
        <div className="search">
          <span>⌕</span>

          <input
            type="text"
            placeholder="Search PO number, PR number, item code, material or supplier"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="chips">
          <button
            className={`btn btn-o btn-sm${filter === "" ? " on" : ""}`}
            onClick={() => setFilter("")}
          >
            All
          </button>
          <button
            className={`btn btn-o btn-sm${filter === "OPEN" ? " on" : ""}`}
            onClick={() => setFilter("OPEN")}
          >
            Open
          </button>
          <button
            className={`btn btn-o btn-sm${filter === "OVERDUE" ? " on" : ""}`}
            onClick={() => setFilter("OVERDUE")}
          >
            Overdue
          </button>
          <button
            className={`btn btn-o btn-sm${filter === "DUE_SOON" ? " on" : ""}`}
            onClick={() => setFilter("DUE_SOON")}
          >
            Due in 7 Days
          </button>
          <button
            className={`btn btn-o btn-sm${filter === "PARTIALLY_RECEIVED" ? " on" : ""}`}
            onClick={() => setFilter("PARTIALLY_RECEIVED")}
          >
            Partially Received
          </button>
          <button
            className={`btn btn-o btn-sm${filter === "FULLY_RECEIVED" ? " on" : ""}`}
            onClick={() => setFilter("FULLY_RECEIVED")}
          >
            Closed
          </button>
        </div>
      </div>

      <div className="card">
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th className="srt" onClick={() => changeSort("po")}>
                  PO Number
                  <span className="ar">{getSortArrow("po")}</span>
                </th>

                <th>PR Number</th>

                <th>Item Code</th>

                <th className="srt" onClick={() => changeSort("material")}>
                  Material Code
                  <span className="ar">{getSortArrow("material")}</span>
                </th>

                <th>Material Description</th>

                <th className="srt" onClick={() => changeSort("vendor")}>
                  Supplier
                  <span className="ar">{getSortArrow("vendor")}</span>
                </th>

                <th className="srt r" onClick={() => changeSort("ordered")}>
                  PO Qty
                  <span className="ar">{getSortArrow("ordered")}</span>
                </th>

                <th className="srt r" onClick={() => changeSort("received")}>
                  Received
                  <span className="ar">{getSortArrow("received")}</span>
                </th>

                <th className="srt r" onClick={() => changeSort("open")}>
                  Open
                  <span className="ar">{getSortArrow("open")}</span>
                </th>

                <th className="srt" onClick={() => changeSort("deliveryDate")}>
                  Delivery date
                  <span className="ar">{getSortArrow("deliveryDate")}</span>
                </th>

                <th className="srt r" onClick={() => changeSort("overdue")}>
                  Overdue
                  <span className="ar">{getSortArrow("overdue")}</span>
                </th>

                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {rows.length > 0 ? (
                rows.map((po) => {
                  const ordered = Number(po.orderQuantity) || 0;
                  const received = Number(po.quantityDelivered) || 0;
                  const open = getOpenQuantity(po);
                  const overdueDays = getOverdueDays(po);
                  const status = getPOStatus(po);
                  const receivedPercentage = getReceivedPercentage(po);

                  return (
                    <tr
                      key={po.openPoId}
                      onClick={() => navigate(`../shortages/${po.material}`)}
                      className="clk"
                    >
                      <td className="mono">{po.purchasingDocument || "—"}</td>

                      <td className="mono dim">
                        {po.purchaseRequisition || (
                          <span className="faint">direct</span>
                        )}
                      </td>

                      <td className="mono">{po.itemCode || "—"}</td>

                      <td className="mono">{po.material || "—"}</td>

                      <td>{po.shortText || "—"}</td>

                      <td>
                        {po.supplierName || "—"}

                        {po.supplierEmail && (
                          <div
                            className="dim"
                            style={{ fontSize: "10.5px" }}
                          >
                            {po.supplierEmail}
                          </div>
                        )}
                      </td>

                      <td className="num">
                        {formatQuantity(ordered)}
                      </td>

                      <td>
                        <div className="tl-bal">
                          <span className="num">
                            {formatQuantity(received)}
                          </span>

                          <span className="bar" style={{ width: "54px" }}>
                            <i
                              style={{
                                width: `${receivedPercentage}%`,
                              }}
                            ></i>
                          </span>
                        </div>
                      </td>

                      <td className={`num ${open > 0 ? "" : "dim"}`}>
                        {open > 0
                          ? formatQuantity(open)
                          : "—"}
                      </td>

                      <td>
                        {/* PO delivery date, else PO creation date + lead time; flagged when there is none */}
                        {po.deliveryDate ? formatDate(po.deliveryDate) : "No delivery date"}
                        {po.deliveryDateBasis === "LEAD_TIME" && (
                          <div className="dim" style={{ fontSize: "10.5px" }}>by lead time</div>
                        )}
                      </td>

                      <td className={`num ${overdueDays > 0 ? "neg" : "dim"}`}>
                        {overdueDays > 0 ? `${overdueDays} days` : "—"}
                      </td>

                      <td>
                        <span className={`bg ${getStatusClass(status)}`}>
                          {getStatusText(status)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="empty">
                    <strong>No orders match.</strong>
                    Try the All filter or change your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrders;
